import { useRef, useState } from 'react';
import {
    Banner,
    BannerDescription,
    BannerFootnote,
    BannerImage,
    BannerImageContainer,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Button,
    FluidImage,
    FormField,
    FormInputGroup,
    Icon,
    StandardForm,
    TextInput,
    tokens,
} from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import FormErrorWrapper from '../../../../components/FormErrorWrapper/FormErrorWrapper';
import { useTrack } from '../../../../utils/analytics';
import { maxKeywordInputLength } from '../../constants';
import { FeaturedNames } from './FeaturedNames';
import styles, { stylesheet } from './Hero.module.scss';

interface HeroProp {
    onSubmit: () => void;
    inputKeyword: string;
    onSearchInputChange: (value: string) => void;
}

const Hero = ({ onSubmit, inputKeyword, onSearchInputChange }: HeroProp) => {
    useStyles(stylesheet);
    const [formErrorMessage, setFormErrorMessage] = useState('');
    const textInputRef = useRef<HTMLInputElement>(null);
    const track = useTrack();

    function trackQuerySubmitted() {
        track('Query Submitted On Business Name Generator', {
            pageSection: 'Hero',
            category: 'Hero',
            label: 'Content:Hero',
            eventDetail: '/business-name-generator;/business-name-generator;Hero;showNameIdeas',
            navigationDetail: 'showNameIdeas',
            query: inputKeyword,
        });
    }

    const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!inputKeyword.trim().length) {
            setFormErrorMessage('Describe your business to generate names.');
            // Move focus to the invalid input and interrupt screen reader if button hit mid-announcement.
            textInputRef.current?.focus();
            return;
        }
        if (formErrorMessage) {
            textInputRef.current?.focus();
            return;
        }
        trackQuerySubmitted();
        onSubmit();
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (value.length > maxKeywordInputLength) {
            setFormErrorMessage(`Maximum ${maxKeywordInputLength} character limit`);
        } else {
            setFormErrorMessage('');
            onSearchInputChange(value);
        }
    };

    return (
        <Banner variant="full-width-image" darkMode>
            <BannerTextContainer>
                <BannerText>
                    <BannerTitle as="h1">Business name generator</BannerTitle>
                    <BannerDescription marginBottom="to-actions" as="p">
                        Enter your business description to generate name ideas instantly, and all
                        for free!
                    </BannerDescription>
                    <StandardForm onSubmit={handleOnSubmit}>
                        <FormField>
                            <FormInputGroup margin={0}>
                                <TextInput
                                    value={inputKeyword}
                                    onChange={handleOnChange}
                                    placeholder="Cozy cafe serving coffee and homemade pastries"
                                    aria-label="Enter keywords to describe your business"
                                    aria-required
                                    aria-invalid={!!formErrorMessage}
                                    aria-errormessage={
                                        formErrorMessage ? 'keywords-error' : undefined
                                    }
                                    name="keywordDescription"
                                    ref={textInputRef}
                                    className={styles.placeholderText}
                                />
                                <FormErrorWrapper
                                    errorMessage={formErrorMessage}
                                    errorId="keywords-error"
                                />
                                <BannerLinks marginTop={2}>
                                    <Button skin="primary" type="submit">
                                        Show name ideas <Icon iconType="magic" size="standard" />
                                    </Button>
                                </BannerLinks>
                            </FormInputGroup>
                        </FormField>
                    </StandardForm>
                    <BannerFootnote style={{ color: tokens.SwanBaseColorGrey400 }}>
                        You are responsible for any keywords or text you input. The business names
                        are generated by a third-party generative AI service and are suggestions
                        only. The names may be used by other businesses or subject to third-party
                        rights. VistaPrint does not accept any liability or responsibility arising
                        in any way from your use of the business names suggested.
                    </BannerFootnote>
                </BannerText>
            </BannerTextContainer>
            <BannerImageContainer>
                <BannerImage>
                    <FluidImage as="picture">
                        <source
                            srcSet="https://cms.cloudinary.vpsvc.com/image/upload/c_scale,f_auto,w_920/vista-logos-pages/en-US/business-name-generator/BusinessNameGenerator_1840x1000_sm_xs_rev_b.webp 1x, https://cms.cloudinary.vpsvc.com/image/upload/c_scale,f_auto,w_1840/vista-logos-pages/en-US/business-name-generator/BusinessNameGenerator_1840x1000_sm_xs_rev_b.webp 2x"
                            media={`(max-width: 1023px)`}
                        />
                        <source
                            srcSet="https://cms.cloudinary.vpsvc.com/image/upload/c_scale,f_auto,w_1920/vista-logos-pages/en-US/business-name-generator/BusinessNameGenerator_3840x1000_xl_lg_md_rev_b.webp 1x, https://cms.cloudinary.vpsvc.com/image/upload/c_scale,f_auto,w_3840/vista-logos-pages/en-US/business-name-generator/BusinessNameGenerator_3840x1000_xl_lg_md_rev_b.webp 2x"
                            media={`(min-width: 1024px)`}
                        />
                        <img
                            className={styles.heroImage}
                            srcSet="https://cms.cloudinary.vpsvc.com/image/upload/c_scale,f_auto,w_920/vista-logos-pages/en-US/business-name-generator/BusinessNameGenerator_1840x1000_sm_xs_rev_b.webp 1x, https://cms.cloudinary.vpsvc.com/image/upload/c_scale,f_auto,w_1840/vista-logos-pages/en-US/business-name-generator/BusinessNameGenerator_1840x1000_sm_xs_rev_b.webp 2x"
                            alt="A barber shop scene featuring the barber standing in front of a mirror holding a hairdryer close to the head of a client with a short haircut."
                            fetchPriority="high"
                        />
                        <FeaturedNames
                            names={['Fresh Fade Barbers', "Clip 'N Snip", 'Trims & Trends']}
                        />
                    </FluidImage>
                </BannerImage>
            </BannerImageContainer>
        </Banner>
    );
};

export default Hero;
